import React, { useState, useEffect } from 'react';
import { Card, CardContent } from "../components/ui/card";
import { Badge } from "../components/ui/badge";
import { Input } from "../components/ui/input";
import { Button } from "../components/ui/button";
import {
  LogIn, Loader2, BookOpen, Moon, Heart, Clock,
  Sparkles, User, Lock, KeyRound, ArrowLeft
} from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL;

function FamilyAuth({ onLogin }) {
  const [mode, setMode] = useState('login');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [name, setName] = useState('');
  const [inviteCode, setInviteCode] = useState('');
  const [relationship, setRelationship] = useState('family');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true); setError('');
    try {
      const endpoint = mode === 'login' ? '/api/family-auth/login' : '/api/family-auth/register';
      const body = mode === 'login' ? { email, password }
        : { invite_code: inviteCode, name, email, password, relationship };
      const res = await fetch(`${API}${endpoint}`, {
        method: 'POST', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(body),
      });
      const data = await res.json();
      if (!res.ok) {
        const detail = data.detail;
        setError(typeof detail === 'string' ? detail : Array.isArray(detail) ? detail.map(e => e.msg || JSON.stringify(e)).join(' ') : 'Something went wrong');
        return;
      }
      localStorage.setItem('family_token', data.token);
      localStorage.setItem('family_user', JSON.stringify(data.user));
      onLogin(data);
    } catch { setError('Unable to connect. Please try again.'); }
    finally { setLoading(false); }
  };

  return (
    <div className="min-h-screen bg-sand-100 flex items-center justify-center p-4" data-testid="family-auth-page">
      <div className="w-full max-w-md">
        <div className="bg-sand-50 rounded-2xl border border-sand-200 p-8 sm:p-10">
          <div className="text-center mb-8">
            <div className="w-14 h-14 rounded-2xl bg-sage-50 flex items-center justify-center mx-auto mb-3">
              <Heart className="w-7 h-7 text-sage-400" />
            </div>
            <h1 className="text-xl font-heading font-bold text-[#2C3D30]">Goodwin Cares</h1>
            <p className="text-sm text-[#5C6A5F] mt-1">Family Portal</p>
          </div>

          <div className="flex gap-1 mb-6 bg-sand-200 rounded-full p-1">
            <button onClick={() => { setMode('login'); setError(''); }}
              className={`flex-1 py-2 rounded-full text-sm font-medium transition-all duration-300 ${mode === 'login' ? 'bg-sand-50 text-sage-400 shadow-sm' : 'text-[#5C6A5F]'}`}
              data-testid="family-login-tab">Sign In</button>
            <button onClick={() => { setMode('register'); setError(''); }}
              className={`flex-1 py-2 rounded-full text-sm font-medium transition-all duration-300 ${mode === 'register' ? 'bg-sand-50 text-sage-400 shadow-sm' : 'text-[#5C6A5F]'}`}
              data-testid="family-register-tab">Register</button>
          </div>

          <form onSubmit={handleSubmit} className="space-y-3">
            {mode === 'register' && (
              <>
                <div className="relative">
                  <KeyRound className="absolute left-3.5 top-2.5 w-4 h-4 text-sand-400" />
                  <Input value={inviteCode} onChange={e => setInviteCode(e.target.value)}
                    placeholder="Invite Code" className="pl-10 rounded-xl border-sand-200 bg-sand-100 focus:border-sage-400" required data-testid="family-invite-code" />
                </div>
                <div className="relative">
                  <User className="absolute left-3.5 top-2.5 w-4 h-4 text-sand-400" />
                  <Input value={name} onChange={e => setName(e.target.value)}
                    placeholder="Your Name" className="pl-10 rounded-xl border-sand-200 bg-sand-100 focus:border-sage-400" required data-testid="family-name" />
                </div>
                <select value={relationship} onChange={e => setRelationship(e.target.value)}
                  className="w-full rounded-xl border border-sand-200 px-4 py-2.5 text-sm bg-sand-100"
                  data-testid="family-relationship">
                  <option value="spouse">Spouse</option>
                  <option value="child">Son/Daughter</option>
                  <option value="parent">Parent</option>
                  <option value="sibling">Sibling</option>
                  <option value="family">Other Family</option>
                  <option value="friend">Close Friend</option>
                </select>
              </>
            )}
            <Input type="email" value={email} onChange={e => setEmail(e.target.value)}
              placeholder="Email" className="rounded-xl border-sand-200 bg-sand-100 focus:border-sage-400" required data-testid="family-email" />
            <div className="relative">
              <Lock className="absolute left-3.5 top-2.5 w-4 h-4 text-sand-400" />
              <Input type="password" value={password} onChange={e => setPassword(e.target.value)}
                placeholder="Password" className="pl-10 rounded-xl border-sand-200 bg-sand-100 focus:border-sage-400" required data-testid="family-password" />
            </div>
            {error && <p className="text-sm text-terra-400 bg-terra-50 p-3 rounded-xl" data-testid="family-auth-error">{error}</p>}
            <Button type="submit" disabled={loading} className="w-full rounded-full bg-sage-400 hover:bg-sage-500 text-white py-2.5" data-testid="family-submit-btn">
              {loading ? <Loader2 className="w-4 h-4 animate-spin mr-1.5" /> : <LogIn className="w-4 h-4 mr-1.5" />}
              {mode === 'login' ? 'Sign In' : 'Create Account'}
            </Button>
          </form>

          {mode === 'register' && (
            <p className="text-xs text-[#5C6A5F] mt-4 text-center">Ask your care team for an invite code to get started.</p>
          )}
        </div>
      </div>
    </div>
  );
}

function FamilyDashboard({ user, token, onLogout }) {
  const [activeTab, setActiveTab] = useState('goodnight');
  const [goodnightChecks, setGoodnightChecks] = useState([]);
  const [memories, setMemories] = useState([]);
  const [lifeStories, setLifeStories] = useState([]);
  const [patient, setPatient] = useState(null);
  const [loading, setLoading] = useState(true);

  const headers = { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json' };

  useEffect(() => {
    const load = async () => {
      setLoading(true);
      try {
        const [patRes, gnRes, memRes, lsRes] = await Promise.all([
          fetch(`${API}/api/family-auth/my-patient`, { headers }),
          fetch(`${API}/api/family-auth/my-goodnight-checks`, { headers }),
          fetch(`${API}/api/family-auth/my-memories`, { headers }),
          fetch(`${API}/api/family-auth/my-life-stories`, { headers }),
        ]);
        if (patRes.ok) setPatient(await patRes.json());
        if (gnRes.ok) { const d = await gnRes.json(); setGoodnightChecks(d.checks || []); }
        if (memRes.ok) { const d = await memRes.json(); setMemories(d.entries || []); }
        if (lsRes.ok) { const d = await lsRes.json(); setLifeStories(d.stories || []); }
      } catch {}
      setLoading(false);
    };
    load();
  }, []);

  const tabs = [
    { id: 'goodnight', label: 'Evening Updates', icon: Moon, count: goodnightChecks.length },
    { id: 'memories', label: 'Memory Book', icon: BookOpen, count: memories.length },
    { id: 'stories', label: 'Life Stories', icon: Sparkles, count: lifeStories.length },
  ];

  if (loading) return (
    <div className="min-h-screen bg-sand-100 flex items-center justify-center">
      <Loader2 className="w-8 h-8 animate-spin text-sage-400" />
    </div>
  );

  return (
    <div className="min-h-screen bg-sand-100" data-testid="family-dashboard">
      <div className="max-w-3xl mx-auto p-6 sm:p-8">
        <div className="flex items-center justify-between mb-8">
          <div>
            <h1 className="text-xl font-heading font-bold text-[#2C3D30]">Welcome, {user.name}</h1>
            {patient && (
              <p className="text-sm text-[#5C6A5F] mt-0.5">
                Caring for <span className="font-medium text-sage-400">{patient.name}</span>
                {patient.assigned_rncm && <span> &middot; RN: {patient.assigned_rncm}</span>}
              </p>
            )}
          </div>
          <Button variant="outline" onClick={onLogout} className="text-xs rounded-full border-sand-200" data-testid="family-logout-btn">
            <ArrowLeft className="w-3.5 h-3.5 mr-1" /> Sign Out
          </Button>
        </div>

        <div className="flex gap-2 mb-6 pb-4 border-b border-sand-200">
          {tabs.map(tab => (
            <button key={tab.id} onClick={() => setActiveTab(tab.id)}
              className={`flex items-center gap-1.5 px-5 py-2.5 rounded-full text-sm font-medium transition-all duration-300 ${
                activeTab === tab.id ? 'bg-sage-50 text-sage-400 border border-sage-200' : 'text-[#5C6A5F] hover:bg-sand-200'
              }`} data-testid={`family-tab-${tab.id}`}>
              <tab.icon className="w-4 h-4" />
              {tab.label}
              {tab.count > 0 && <Badge variant="outline" className="ml-1 text-xs rounded-full border-sand-200">{tab.count}</Badge>}
            </button>
          ))}
        </div>

        {activeTab === 'goodnight' && (
          <div className="space-y-3" data-testid="family-goodnight-list">
            {goodnightChecks.length === 0 ? (
              <div className="text-center py-16 text-[#5C6A5F]">
                <Moon className="w-10 h-10 mx-auto mb-3 text-sand-300" />
                <p className="text-sm">No evening updates yet. They'll appear here each evening.</p>
              </div>
            ) : goodnightChecks.map(check => (
              <div key={check.id} className="p-5 rounded-2xl bg-sand-50 border border-sand-200 border-l-4 border-l-sage-400" data-testid={`gn-check-${check.id}`}>
                <div className="flex items-center justify-between mb-2">
                  <div className="flex gap-2">
                    {check.pain_level != null && <Badge variant="outline" className="text-xs rounded-full border-sand-200">Pain: {check.pain_level}/10</Badge>}
                    {check.pps_score != null && <Badge variant="outline" className="text-xs rounded-full border-sand-200">PPS: {check.pps_score}</Badge>}
                  </div>
                  <span className="text-xs text-[#5C6A5F]">{new Date(check.generated_at).toLocaleDateString()}</span>
                </div>
                <p className="text-sm text-[#2C3D30] leading-relaxed whitespace-pre-wrap">{check.summary}</p>
              </div>
            ))}
          </div>
        )}

        {activeTab === 'memories' && (
          <div className="space-y-3" data-testid="family-memories-list">
            {memories.length === 0 ? (
              <div className="text-center py-16 text-[#5C6A5F]">
                <BookOpen className="w-10 h-10 mx-auto mb-3 text-sand-300" />
                <p className="text-sm">No memories captured yet.</p>
              </div>
            ) : memories.map(entry => (
              <div key={entry.id} className="p-5 rounded-2xl bg-sand-50 border border-sand-200 border-l-4 border-l-terra-400" data-testid={`memory-${entry.id}`}>
                <div className="flex items-center justify-between mb-1">
                  <h3 className="text-sm font-heading font-semibold text-[#2C3D30]">{entry.title}</h3>
                  <Badge className="bg-terra-50 text-terra-400 rounded-full text-xs">{entry.memory_type}</Badge>
                </div>
                {entry.content && <p className="text-sm text-[#5C6A5F] leading-relaxed mt-2 whitespace-pre-wrap">{entry.content}</p>}
                <p className="text-xs text-sand-400 mt-3 flex items-center gap-1">
                  <Clock className="w-3 h-3" /> {new Date(entry.created_at).toLocaleDateString()}
                  {entry.date_of_memory && <span> &middot; Memory from {entry.date_of_memory}</span>}
                </p>
              </div>
            ))}
          </div>
        )}

        {activeTab === 'stories' && (
          <div className="space-y-3" data-testid="family-stories-list">
            {lifeStories.length === 0 ? (
              <div className="text-center py-16 text-[#5C6A5F]">
                <Sparkles className="w-10 h-10 mx-auto mb-3 text-sand-300" />
                <p className="text-sm">No life stories generated yet. Ask your care team to help create one.</p>
              </div>
            ) : lifeStories.map(story => (
              <div key={story.id} className="p-5 rounded-2xl bg-sand-50 border border-sand-200 border-l-4 border-l-sky-300" data-testid={`story-${story.id}`}>
                <div className="flex items-center justify-between mb-2">
                  <span className="text-sm font-heading font-semibold text-[#2C3D30]">{story.patient_name}'s Life Story</span>
                  <Badge className="bg-sky-50 text-sky-500 rounded-full text-xs">{story.word_count} words</Badge>
                </div>
                <p className="text-sm text-[#2C3D30] leading-relaxed whitespace-pre-wrap">{story.story_text}</p>
                <p className="text-xs text-sand-400 mt-3 flex items-center gap-1">
                  <Clock className="w-3 h-3" /> {new Date(story.generated_at).toLocaleString()}
                </p>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

export default function FamilyPortalPage() {
  const [authData, setAuthData] = useState(null);
  const [checking, setChecking] = useState(true);

  useEffect(() => {
    const token = localStorage.getItem('family_token');
    const user = localStorage.getItem('family_user');
    if (token && user) {
      try { setAuthData({ token, user: JSON.parse(user) }); }
      catch { localStorage.removeItem('family_token'); localStorage.removeItem('family_user'); }
    }
    setChecking(false);
  }, []);

  if (checking) return null;
  if (!authData) return <FamilyAuth onLogin={setAuthData} />;
  return <FamilyDashboard user={authData.user} token={authData.token}
    onLogout={() => { localStorage.removeItem('family_token'); localStorage.removeItem('family_user'); setAuthData(null); }} />;
}
